import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"

import { cn } from "@/lib/utils"
import { Loader2 } from "lucide-react"

const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-md cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed shadow-xs font-medium",
  {
    variants: {
      variant: {
        default: "bg-black text-white enabled:hover:bg-gray-800 border border-transparent",
        outline: "border border-gray-950 bg-transparent text-gray-950 hover:bg-gray-100",
        ghost: "bg-transparent text-gray-950 hover:bg-gray-200 shadow-none border border-transparent",
      },
      size: {
        default: "py-2 px-6 text-sm leading-5",
        sm: "py-1.5 px-2.5 text-xs leading-4",
        lg: "py-3 px-6 text-base leading-6",
        xl: "py-4 px-8 text-lg leading-7",
        icon: "p-2 h-10 w-10 text-sm",
        custom: '',
      },
      skin: {
        destructive: "border-primary-500",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
    compoundVariants: [
      {
        variant: "outline",
        skin: "destructive",
        className: "border-primary-500 text-primary-500 hover:bg-primary-100",
      },
      {
        variant: "default",
        skin: "destructive",
        className: "bg-primary-500 enabled:hover:bg-primary-600",
      },
    ],
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  loading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
        {children}
      </Comp>
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
